import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Reception from "@/pages/Reception";
import Dealer from "@/pages/Dealer";
import TablesPage from "@/pages/TablesPage";
import PlayersPage from "@/pages/PlayersPage";
import CheckinPage from "@/pages/Checkin";
import AdminGate, { AdminRoute } from "@/pages/Admin";
import { Dashboard, isAdmin, clearAdminPin } from "@/lib/api";
import { fmtBRL } from "@/lib/format";
import { SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGO_URL = "/mixology-logo.jpeg";

const HeaderStat = ({ label, value, testId }) => (
    <div className="hidden md:flex flex-col items-end px-5 border-l border-border/60">
        <span className="label-mini">{label}</span>
        <span data-testid={testId} className="metric-num text-xl text-foreground mt-0.5">{value}</span>
    </div>
);

const Shell = ({ children }) => {
    const [stats, setStats] = useState(null);
    const admin = isAdmin();
    const load = useCallback(() => {
        if (!isAdmin()) { setStats(null); return; }
        Dashboard.summary().then(setStats).catch(() => {});
    }, []);
    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [load]);

    const publicNav = [{ to: "/checkin", label: "Check-in", testId: "nav-checkin" }];
    const adminNav = [
        { to: "/checkin", label: "Check-in", testId: "nav-checkin" },
        { to: "/reception", label: "Reception", testId: "nav-reception" },
        { to: "/tables", label: "Tables", testId: "nav-tables" },
        { to: "/dealer", label: "Dealer", testId: "nav-dealer" },
        { to: "/players", label: "Players", testId: "nav-players" },
    ];
    const nav = admin ? adminNav : publicNav;

    const signOut = () => {
        clearAdminPin();
        toast.success("Signed out");
        window.location.href = "/checkin";
    };

    return (
        <div className="min-h-screen">
            <header
                data-testid="app-header"
                className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border"
            >
                <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-6">
                    <div className="flex items-center" data-testid="brand-container">
                        <img
                            src={LOGO_URL}
                            alt="Mixology Lounge"
                            data-testid="brand-logo"
                            className="h-14 md:h-16 w-auto object-contain"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const fb = e.currentTarget.nextSibling;
                                if (fb) fb.style.display = "block";
                            }}
                        />
                        <div
                            data-testid="brand-fallback"
                            style={{ display: "none" }}
                            className="font-serif text-2xl tracking-[0.35em] text-foreground"
                        >
                            MIXOLOGY <span className="text-gold">LOUNGE</span>
                        </div>
                    </div>

                    <nav className="ml-6 flex items-center gap-1">
                        {nav.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={n.testId}
                                className={({ isActive }) =>
                                    `px-4 py-2 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-secondary text-foreground border border-gold/30"
                                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="ml-auto flex items-center gap-3">
                        {admin && (
                            <>
                                <HeaderStat testId="hdr-active-tables" label="Active" value={stats?.active_tables ?? "—"} />
                                <HeaderStat testId="hdr-seated" label="Seated" value={stats?.total_seated ?? "—"} />
                                <HeaderStat testId="hdr-waitlist" label="Waitlist" value={stats?.total_waitlist ?? "—"} />
                                <HeaderStat testId="hdr-rake" label="Rake" value={stats ? fmtBRL(stats.total_rake) : "—"} />
                                <button
                                    onClick={signOut}
                                    data-testid="btn-header-signout"
                                    className="ml-3 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-2 flex items-center gap-1.5 transition-colors"
                                >
                                    <SignOut size={14} /> Sign out
                                </button>
                            </>
                        )}
                        {!admin && (
                            <NavLink to="/admin" data-testid="nav-admin" className="text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-2 transition-colors">
                                Staff sign-in
                            </NavLink>
                        )}
                    </div>
                </div>
            </header>
            <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 animate-fade-up">
                {children}
            </main>
        </div>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Toaster position="top-right" theme="dark" richColors />
                <Shell>
                    <Routes>
                        <Route path="/" element={<Navigate to="/checkin" replace />} />
                        <Route path="/checkin" element={<CheckinPage />} />
                        <Route path="/admin" element={<AdminGate />} />
                        <Route path="/reception" element={<AdminRoute><Reception /></AdminRoute>} />
                        <Route path="/tables" element={<AdminRoute><TablesPage /></AdminRoute>} />
                        <Route path="/tables/:tableId" element={<AdminRoute><Dealer /></AdminRoute>} />
                        <Route path="/dealer" element={<AdminRoute><Dealer /></AdminRoute>} />
                        <Route path="/players" element={<AdminRoute><PlayersPage /></AdminRoute>} />
                        <Route path="*" element={<Navigate to="/checkin" replace />} />
                    </Routes>
                </Shell>
            </BrowserRouter>
        </div>
    );
}

export default App;
